// Felles skjemadeler i startsidens stil (planer/ny-kundesamhandling-plan.md): tilbake-lenke,
// sidetopp med ikon, tekstfelt, nedtrekksliste og valgknapper. Brukes av ordresidene
// (CreateOrder, OrderList, OrderDetails), så felt og knapper ser like ut overalt.
//
// Sidebredde (vinnsel, 30.10): alle sider i det nye designet bruker samme ytre beholder,
// `mx-auto max-w-7xl` (1280 px), også skjemaer og «Laster …»/«Fant ikke»-visninger.
import React from 'react';
import { Link } from 'react-router-dom';
import { FARGER, FORSIDE } from './handlinger';
import Ikon from './Ikon';

export const feltKlasse =
  'w-full rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 text-base focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30';

export const knappPrimar =
  'inline-flex min-h-[2.5rem] items-center justify-center gap-2 rounded-xl bg-emerald-700 px-3 py-1.5 text-base font-bold text-white hover:bg-emerald-800 active:scale-[0.98] touch-manipulation disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50';

export const knappSekundar =
  'inline-flex min-h-[2.5rem] items-center justify-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-800 hover:bg-slate-50 active:scale-[0.98] touch-manipulation disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50';

export function Tilbake({ til = FORSIDE, children = 'Tilbake til kontrollpanelet' }) {
  return (
    <Link to={til} className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 font-medium text-indigo-700 hover:bg-indigo-50">
      <Ikon navn="tilbake" className="w-5 h-5" /> {children}
    </Link>
  );
}

export function Sidetopp({ ikon, farge = 'info', tittel, undertittel, children }) {
  const f = FARGER[farge] || FARGER.info;
  return (
    <header className="flex flex-wrap items-center gap-3">
      <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${f.ikon}`}>
        <Ikon navn={ikon} className="w-6 h-6" />
      </span>
      <div className="min-w-0 flex-1 basis-56">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 text-balance break-words">{tittel}</h1>
        {undertittel && <div className="text-slate-600 [overflow-wrap:anywhere]">{undertittel}</div>}
      </div>
      {children}
    </header>
  );
}

export function Tekstfelt({ id, etikett, hjelp, flerlinjet = false, className = '', ...rest }) {
  const Felt = flerlinjet ? 'textarea' : 'input';
  return (
    <div className={`min-w-0 ${className}`}>
      <label htmlFor={id} className="mb-1 block font-semibold text-slate-800">
        {etikett}
        {rest.required && (
          <>
            <span aria-hidden="true" className="text-red-700"> *</span>
            <span className="sr-only"> (må fylles ut)</span>
          </>
        )}
      </label>
      <Felt
        id={id}
        {...(flerlinjet ? { rows: 3 } : { type: 'text' })}
        aria-describedby={hjelp ? `${id}-hjelp` : undefined}
        className={feltKlasse}
        {...rest}
      />
      {hjelp && <p id={`${id}-hjelp`} className="mt-1 text-sm text-slate-600">{hjelp}</p>}
    </div>
  );
}

export function Valgfelt({ id, etikett, children, className = '', ...rest }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <label htmlFor={id} className="mb-1 block font-semibold text-slate-800">{etikett}</label>
      <select id={id} className={feltKlasse} {...rest}>
        {children}
      </select>
    </div>
  );
}

// Knapper der én er valgt (aria-pressed), f.eks. status.
export function Valgknapper({ etikett, id, valg, verdi, onVelg }) {
  return (
    <div role="group" aria-labelledby={id} className="space-y-1.5">
      <span id={id} className="block font-semibold text-slate-800">{etikett}</span>
      <div className="flex flex-wrap gap-2">
        {valg.map((v) => (
          <button
            key={v.verdi}
            type="button"
            aria-pressed={verdi === v.verdi}
            onClick={() => onVelg(v.verdi)}
            className={`min-h-[2.25rem] rounded-xl border-2 px-3 py-1.5 font-semibold transition touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${
              verdi === v.verdi ? 'border-indigo-700 bg-indigo-700 text-white' : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
            }`}
          >
            {v.tittel}
          </button>
        ))}
      </div>
    </div>
  );
}

// Ordrestatusene. «AktivKLUBB» er lagret verdi; vises som «Aktiv Klubb-bestilling» (vinnsel, 02.11),
// og står også i egen tabell i trykklisten (/service) så klubbansvarlig snapper dem opp.
export const ORDRESTATUSER = [
  { verdi: 'Aktiv', tittel: 'Aktiv' },
  { verdi: 'AktivKLUBB', tittel: 'Aktiv Klubb-bestilling' },
  { verdi: 'Avsluttet', tittel: 'Avsluttet' },
];
export const ordreStatusTekst = (s) => ORDRESTATUSER.find((x) => x.verdi === s)?.tittel || s || 'Aktiv';
